/**
 * Sound effects (docs/SPEC.md section 3, the Sound effects switch in Settings). Short synthesised blips: no sound files to load.
 * Nothing plays until the player has touched the game (browsers forbid sound before a gesture), nothing plays while the switch is off,
 * and a browser without audio, or an audio context that throws, simply makes no sound. The browser pieces are passed in for tests.
 */
export type Sound = "care-food" | "care-rest" | "care-play" | "care-clean" | "care-love" | "hatch" | "badge" | "last-chance";

/** The part of the Web Audio API this module uses. */
export interface AudioLike {
  readonly currentTime: number;
  readonly state: string;
  resume(): Promise<void> | void;
  createOscillator(): {
    type: string;
    frequency: { setValueAtTime(value: number, time: number): void };
    connect(node: unknown): unknown;
    start(time: number): void;
    stop(time: number): void;
  };
  createGain(): {
    gain: { setValueAtTime(value: number, time: number): void; linearRampToValueAtTime(value: number, time: number): void };
    connect(node: unknown): unknown;
  };
  readonly destination: unknown;
}

interface Note {
  type: "sine" | "triangle";
  /** Hz, played one after another. */
  freq: number[];
  /** Length of each note in seconds. */
  step: number;
  /** Peak gain: quiet on purpose. */
  peak: number;
}

/** Each sound is a few notes. The last-chance chime is gentle (soft triangle, low register), not an alarm. */
const NOTES: Record<Sound, Note> = {
  "care-food": { type: "triangle", freq: [523, 659], step: 0.09, peak: 0.08 },
  "care-rest": { type: "sine", freq: [392, 330], step: 0.12, peak: 0.08 },
  "care-play": { type: "triangle", freq: [659, 784, 880], step: 0.07, peak: 0.08 },
  "care-clean": { type: "sine", freq: [880, 1047], step: 0.06, peak: 0.07 },
  "care-love": { type: "triangle", freq: [587, 740], step: 0.1, peak: 0.08 },
  hatch: { type: "triangle", freq: [523, 659, 784, 1047], step: 0.12, peak: 0.1 },
  badge: { type: "sine", freq: [784, 988, 1175], step: 0.1, peak: 0.09 },
  "last-chance": { type: "triangle", freq: [330, 294], step: 0.25, peak: 0.07 },
};

export interface SoundDeps {
  /** Reads the Sound effects switch at the time of the sound. */
  enabled(): boolean;
  /** Makes the audio context, or null when the browser has none. May throw. */
  create(): AudioLike | null;
}

export function createSound(deps: SoundDeps) {
  let ctx: AudioLike | null = null;
  let tried = false;

  function context(): AudioLike | null {
    if (!tried) {
      tried = true;
      try {
        ctx = deps.create();
      } catch {
        ctx = null;
      }
    }
    return ctx;
  }

  return {
    /** Call from a press (a tap or a click). Makes the context and wakes it if the browser had put it to sleep. */
    unlock() {
      const c = context();
      if (c && c.state === "suspended") void c.resume();
    },

    /** Plays one sound now, if the switch is on and the player has touched the game. */
    play(name: Sound) {
      if (!ctx || !deps.enabled()) return;
      try {
        const note = NOTES[name];
        const t0 = ctx.currentTime;
        note.freq.forEach((f, i) => {
          const start = t0 + i * note.step;
          const end = start + note.step;
          const osc = ctx!.createOscillator();
          const gain = ctx!.createGain();
          osc.type = note.type;
          osc.frequency.setValueAtTime(f, start);
          gain.gain.setValueAtTime(0, start);
          gain.gain.linearRampToValueAtTime(note.peak, start + 0.01);
          gain.gain.linearRampToValueAtTime(0, end);
          osc.connect(gain);
          gain.connect(ctx!.destination);
          osc.start(start);
          osc.stop(end);
        });
      } catch {
        // No sound is better than an error in the game.
      }
    },
  };
}

export type SoundPlayer = ReturnType<typeof createSound>;

/** Which sound a game message makes, if any: a new badge chimes, the last-chance warning chimes gently, everything else is quiet. */
export function soundForToast(text: string): "badge" | "last-chance" | null {
  if (text.startsWith("New badge")) return "badge";
  if (text.endsWith("needs you now!")) return "last-chance";
  return null;
}
